import { type ReactNode } from 'react';

import { Box, Typography } from '@mui/material';

interface PageHeadingProps {
  /** Main page title */
  title: string;
  /** Optional subtitle/description */
  subtitle?: string;
  /** Optional action button(s) to display on the right */
  action?: ReactNode;
}

/**
 * Standardized page heading component
 * Ensures consistent typography across all pages
 *
 * Design spec (matching dataProtect):
 * - Title: 20px, font-weight 400 (normal)
 * - Subtitle: 13px, text.secondary
 * - Avenir font family (from theme)
 */
export const PageHeading = ({ title, subtitle, action }: PageHeadingProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: subtitle ? 'flex-start' : 'center',
        mb: 3,
      }}
    >
      <Box>
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontSize: '20px',
            fontWeight: 400,
            lineHeight: 1.2,
            mb: subtitle ? 1 : 0,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {action && <Box>{action}</Box>}
    </Box>
  );
};
